/**
 * @fileoverview i18n configuration for KaraoNow frontend.
 * @summary Configures i18next with 8 languages and backend synchronization.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/i18n
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { fr } from './locales/fr.js'
import { en } from './locales/en.js'
import { es } from './locales/es.js'
import { it } from './locales/it.js'
import { pt } from './locales/pt.js'
import { ja } from './locales/ja.js'
import { zh } from './locales/zh.js'
import { ko } from './locales/ko.js'

/**
 * @summary Supported languages.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export type SupportedLanguage = 'fr' | 'en' | 'es' | 'it' | 'pt' | 'ja' | 'zh' | 'ko'
export const SUPPORTED_LANGUAGES = ['fr', 'en', 'es', 'it', 'pt', 'ja', 'zh', 'ko'] as const

/**
 * @summary Map i18n language code to locale string for date formatting.
 * @param {string} langCode - i18n language code (e.g., 'fr', 'en').
 * @returns {string} Locale string (e.g., 'fr-FR', 'en-US').
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function getLocaleFromLanguage(langCode: string): string {
  const localeMap: Record<SupportedLanguage, string> = {
    fr: 'fr-FR',
    en: 'en-US',
    es: 'es-ES',
    it: 'it-IT',
    pt: 'pt-BR',
    ja: 'ja-JP',
    zh: 'zh-CN',
    ko: 'ko-KR',
  }
  return localeMap[langCode as SupportedLanguage] || 'fr-FR'
}

/**
 * @summary Initialize i18next.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      fr: { translation: fr },
      en: { translation: en },
      es: { translation: es },
      it: { translation: it },
      pt: { translation: pt },
      ja: { translation: ja },
      zh: { translation: zh },
      ko: { translation: ko },
    },
    fallbackLng: 'en',
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  })

export default i18n
